import { customElement, property } from 'lit/decorators.js';
import { ExtBaseElement } from '../../core/ExtBaseElement';
import './ext-icon.css';

export type ExtIconSize = 'sm' | 'md' | 'lg';

interface LucideGlobal {
  createIcons?: (opts?: { icons?: unknown; nameAttr?: string; attrs?: Record<string, string> } | HTMLElement) => void;
}

/**
 * <ext-icon name="check" size="md" color="#ff0000">
 *
 * Wrapper alrededor de Lucide Icons (cargado globalmente via UMD en el layout
 * de extranet_admin: window.lucide). Al conectarse, inserta un
 * <i data-lucide="{name}"> y llama a window.lucide.createIcons() scoped a si
 * mismo para que Lucide lo reemplace por el SVG inline.
 *
 * Light DOM: el <ext-icon> ES el contenedor del svg; aplicamos tamaño y color
 * via CSS. No usamos <slot>: el contenido es siempre el <i> que lucide
 * convierte en <svg>.
 *
 * Props:
 *  - name: string (requerido) nombre lucide (ej "check", "x", "trash-2").
 *  - size: sm | md | lg (default md). 14 / 18 / 24 px.
 *  - color: string (opcional) override; default currentColor (hereda del padre).
 *
 * Gotcha: si window.lucide aun no cargo (script con defer), intentamos de nuevo
 * en el proximo frame. Solo un retry suave para no bloquear; si lucide falla
 * en cargar, dejamos el <i> con data-lucide y Lucide lo rescatara cuando
 * cualquier otro createIcons() global corra.
 */
@customElement('ext-icon')
export class ExtIcon extends ExtBaseElement {
  @property({ type: String, reflect: true }) name = '';
  @property({ type: String, reflect: true }) size: ExtIconSize = 'md';
  @property({ type: String }) color = '';

  private _retried = false;

  protected createRenderRoot(): HTMLElement {
    return this;
  }

  render(): null {
    return null;
  }

  connectedCallback(): void {
    super.connectedCallback();
    this._renderIcon();
  }

  protected updated(changed: Map<string, unknown>): void {
    if (changed.has('name') || changed.has('color')) {
      this._renderIcon();
    }
  }

  private _renderIcon(): void {
    // Limpiar contenido previo (si el name cambio).
    this.innerHTML = '';
    if (!this.name) return;

    const i = document.createElement('i');
    i.setAttribute('data-lucide', this.name);
    i.setAttribute('aria-hidden', 'true');
    if (this.color) {
      i.style.color = this.color;
    }
    this.appendChild(i);

    this._invokeLucide();
  }

  private _invokeLucide(): void {
    const lucide = (window as unknown as { lucide?: LucideGlobal }).lucide;
    if (lucide && typeof lucide.createIcons === 'function') {
      try {
        // Signature clasica: createIcons({ attrs, nameAttr, icons }); ademas
        // createIcons() sin args escanea document entero. Para scoping
        // pasamos un subtree: la lib soporta pasar un Element como root
        // en builds recientes; si no, cae al escaneo global (harmless).
        lucide.createIcons({});
      } catch {
        // noop
      }
      return;
    }
    // lucide aun no disponible. Reintento suave en el siguiente frame.
    if (!this._retried) {
      this._retried = true;
      requestAnimationFrame(() => this._invokeLucide());
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ext-icon': ExtIcon;
  }
}
